import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

function Header() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [showCompanyMenu, setShowCompanyMenu] = useState(false);
  const [companyLanguage, setCompanyLanguage] = useState('en');
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Check if company is logged in
    const token = localStorage.getItem("companyToken");
    const storedCompanyData = localStorage.getItem("companyData");
    
    if (token && storedCompanyData) {
      try {
        const company = JSON.parse(storedCompanyData);
        setCompanyData(company);
        // Store company language in localStorage for global access
        // Priority: websiteLanguage > language > localStorage > 'en'
        const storedLang = localStorage.getItem("companyLanguage");
        const lang = company.websiteLanguage || company.language || storedLang || 'en';
        setCompanyLanguage(lang);
        localStorage.setItem("companyLanguage", lang);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
        console.error("Error parsing company data:", error);
        }
      }
    } else {
      // Clear language if not logged in
      localStorage.removeItem("companyLanguage");
      setCompanyLanguage('en');
    }
  }, []);

  // Listen for storage changes to update language when admin changes it
  useEffect(() => {
    const handleStorageChange = () => {
      const storedCompanyData = localStorage.getItem("companyData");
      if (storedCompanyData) {
        try {
          const company = JSON.parse(storedCompanyData);
          const lang = company.websiteLanguage || company.language || 'en';
          setCompanyLanguage(lang);
          localStorage.setItem("companyLanguage", lang);
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
          console.error("Error parsing company data:", error);
        }
        }
      }
    };

    // Listen for custom event when company data is updated
    window.addEventListener('companyDataUpdated', handleStorageChange);
    
    // Also check periodically (every 2 seconds) if company data changed
    const interval = setInterval(() => {
      const storedCompanyData = localStorage.getItem("companyData");
      if (storedCompanyData) {
        try {
          const company = JSON.parse(storedCompanyData);
          const currentLang = company.websiteLanguage || company.language || 'en';
          if (currentLang !== companyLanguage) {
            setCompanyLanguage(currentLang);
            localStorage.setItem("companyLanguage", currentLang);
          }
        } catch (error) {
          // Ignore
        }
      }
    }, 2000);

    return () => {
      window.removeEventListener('companyDataUpdated', handleStorageChange);
      clearInterval(interval);
    };
  }, [companyLanguage]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCompanyMenu(false);
      }
    };

    if (showCompanyMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCompanyMenu]);

  const handleCompanyLogout = () => {
    localStorage.removeItem("companyToken");
    localStorage.removeItem("companyData");
    localStorage.removeItem("companyLanguage");
    setCompanyData(null);
    setCompanyLanguage('en');
    toast.info(companyLanguage === 'ur' ? "کامیابی سے لاگ آؤٹ ہو گئے" : "Logged out successfully");
    navigate("/");
  };

  const handleWhatsAppClick = () => {
    // Phone number from footer: 03231099709
    // WhatsApp URL format: https://wa.me/923231099709 (removing leading 0, adding country code 92)
    const phoneNumber = '923231099709';
    const whatsappUrl = `https://wa.me/${phoneNumber}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <header className="bg-brand-header shadow-md sticky top-0 z-50">
      <nav
        className={`container mx-auto px-4 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
        dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}
      >
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 animate-soft-bounce">
            <img 
              src="/logobest.png" 
              alt="Logo" 
              className="h-24 w-24 md:h-26 md:w-26 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <span className="text-2xl font-bold text-brand-button">
              {companyLanguage === 'ur' ? 'منڈی ٹریڈ ای آر پی' : 'Mandi Trade ERP'}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-brand-background hover:text-white font-medium transition">
              {companyLanguage === 'ur' ? 'ہوم' : 'Home'}
            </Link>
            
            <Link to="/portfolio" className="text-brand-background hover:text-white font-medium transition">
              {companyLanguage === 'ur' ? 'فیچرز' : 'Features'}
            </Link>
            
            <Link to="/blog" className="text-brand-background hover:text-white font-medium transition">
              {companyLanguage === 'ur' ? 'رہنمائی' : 'Guides'}
            </Link>
            
            {companyData ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  className="text-brand-background hover:text-white font-medium transition flex items-center gap-2"
                  onClick={() => setShowCompanyMenu(!showCompanyMenu)}
                >
                  <span>{companyData.shopName || companyData.username || 'Company'}</span>
                  <svg className={`w-4 h-4 transition-transform ${showCompanyMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showCompanyMenu && (
                  <div
                    className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-xl py-2 z-50"
                  >
                    <Link 
                      to="/company/dashboard" 
                      className="block px-4 py-2 text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                      onClick={() => setShowCompanyMenu(false)}
                    >
                      {companyLanguage === 'ur' ? 'کمپنی ڈیش بورڈ' : 'Company Dashboard'}
                    </Link>
                    <button
                      onClick={() => {
                        setShowCompanyMenu(false);
                        handleCompanyLogout();
                      }}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                    >
                      {companyLanguage === 'ur' ? 'لاگ آؤٹ' : 'Logout'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/company/login" className="text-brand-background hover:text-white font-medium transition">
                {companyLanguage === 'ur' ? 'کمپنی لاگ اِن' : 'Company Login'}
              </Link>
            )}
            <Link to="/admin/login" className="text-brand-background hover:text-white font-medium transition">
              {companyLanguage === 'ur' ? 'ایڈمن' : 'Admin'}
            </Link>
            <Link to="/contact" className="bg-brand-button hover:bg-yellow-500 text-gray-900 font-semibold px-6 py-2 rounded-lg transition transform hover:-translate-y-0.5">
              {companyLanguage === 'ur' ? 'رابطہ / ڈیمو' : 'Contact / Demo'}
            </Link>
            
            {/* WhatsApp Contact Button */}
            <button
              onClick={handleWhatsAppClick}
              className="flex items-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold px-4 py-2 rounded-lg transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl hover:scale-105 group relative overflow-hidden"
              title={companyLanguage === 'ur' ? 'واٹس ایپ پر رابطہ کریں' : 'Contact us on WhatsApp'}
            >
              <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></span>
              <svg 
                className="w-5 h-5 relative z-10 group-hover:scale-110 transition-transform duration-300" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              <span className="hidden lg:inline relative z-10">
                {companyLanguage === 'ur' ? 'رابطہ کریں' : 'Contact Us'}
              </span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-brand-background"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 space-y-2">
            <Link to="/" className="block px-4 py-2 text-brand-background hover:bg-brand-card/40 rounded">
              {companyLanguage === 'ur' ? 'ہوم' : 'Home'}
            </Link>
            <Link to="/portfolio" className="block px-4 py-2 text-brand-background hover:bg-brand-card/40 rounded">
              {companyLanguage === 'ur' ? 'فیچرز' : 'Features'}
            </Link>
            <Link to="/blog" className="block px-4 py-2 text-brand-background hover:bg-brand-card/40 rounded">
              {companyLanguage === 'ur' ? 'رہنمائی' : 'Guides'}
            </Link>
            {companyData ? (
              <>
                <Link to="/company/dashboard" className="block px-4 py-2 text-brand-background hover:bg-brand-card/40 rounded">
                  {companyLanguage === 'ur' ? 'کمپنی ڈیش بورڈ' : 'Company Dashboard'}
                </Link>
                <button
                  onClick={handleCompanyLogout}
                  className="w-full text-left block px-4 py-2 text-brand-background hover:bg-brand-card/40 rounded"
                >
                  {companyLanguage === 'ur' ? 'لاگ آؤٹ' : 'Logout'}
                </button>
              </>
            ) : (
              <Link to="/company/login" className="block px-4 py-2 text-brand-background hover:bg-brand-card/40 rounded">
                {companyLanguage === 'ur' ? 'کمپنی لاگ اِن' : 'Company Login'}
              </Link>
            )}
            <Link to="/admin/login" className="block px-4 py-2 text-brand-background hover:bg-brand-card/40 rounded">
              {companyLanguage === 'ur' ? 'ایڈمن لاگ ان' : 'Admin Login'}
            </Link>
            <Link to="/contact" className="block px-4 py-2 bg-brand-button text-gray-900 rounded text-center">
              {companyLanguage === 'ur' ? 'رابطہ / ڈیمو' : 'Contact / Demo'}
            </Link>
            
            {/* WhatsApp Contact Button - Mobile */}
            <button
              onClick={handleWhatsAppClick}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#25D366] hover:bg-[#20BA5A] text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              <svg 
                className="w-5 h-5" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              <span>{companyLanguage === 'ur' ? 'واٹس ایپ پر رابطہ کریں' : 'Contact Us on WhatsApp'}</span>
            </button>
          </div>
        )}
      </nav>
    </header>
  );
}

export default Header;
